import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import Reveal from './Reveal';
import { galleryImages } from '../mock';

const Gallery = () => {
  const [activeIdx, setActiveIdx] = useState(null);

  const close = useCallback(() => setActiveIdx(null), []);
  const next = useCallback((e) => {
    e?.stopPropagation?.();
    setActiveIdx((i) =>
      i === null ? null : (i + 1) % galleryImages.length
    );
  }, []);
  const prev = useCallback((e) => {
    e?.stopPropagation?.();
    setActiveIdx((i) =>
      i === null ? null : (i - 1 + galleryImages.length) % galleryImages.length
    );
  }, []);

  useEffect(() => {
    if (activeIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIdx, close, next, prev]);

  return (
    <section id="gallery" className="py-24 md:py-32 bg-[#f5efe7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="text-center mb-14">
          <p className="text-[#e6396b] tracking-[0.4em] text-[12px] font-semibold mb-5">
            GALLERY
          </p>
          <h2 className="font-serif text-[44px] md:text-[64px] text-[#1a1a1a] font-medium">
            Live Moments
          </h2>
          <p className="mt-4 text-[15px] text-[#6a6a6a]">
            Click any photo to view full size
          </p>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {galleryImages.map((src, idx) => (
            <Reveal key={src} delay={(idx % 4) * 80}>
              <button
                onClick={() => setActiveIdx(idx)}
                className="group relative overflow-hidden rounded-xl shadow-sm aspect-[3/2] w-full focus:outline-none focus:ring-2 focus:ring-[#e6396b]"
              >
                <img
                  src={src}
                  alt={`Live moment ${idx + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 text-[#1a1a1a] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Expand size={15} />
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {activeIdx !== null && (
        <div
          className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4"
          onClick={close}
          style={{ animation: 'fadeIn 0.3s ease' }}
        >
          <button
            onClick={close}
            className="absolute top-5 right-5 text-white/90 hover:text-white p-2"
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <button
            onClick={prev}
            className="absolute left-4 md:left-10 text-white/80 hover:text-white p-3 bg-white/10 hover:bg-white/20 rounded-full"
            aria-label="Previous"
          >
            <ChevronLeft size={28} />
          </button>
          <img
            src={galleryImages[activeIdx]}
            alt="Full view"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-md shadow-2xl"
          />
          <button
            onClick={next}
            className="absolute right-4 md:right-10 text-white/80 hover:text-white p-3 bg-white/10 hover:bg-white/20 rounded-full"
            aria-label="Next"
          >
            <ChevronRight size={28} />
          </button>
          <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70 text-sm tracking-[0.2em]">
            {String(activeIdx + 1).padStart(2, '0')} /{' '}
            {String(galleryImages.length).padStart(2, '0')}
          </p>
        </div>
      )}
    </section>
  );
};

export default Gallery;
